import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Badge, Button, Card, Alert, Form, Spinner } from 'react-bootstrap';
import DatePeriodNavigation, { PeriodChangeEvent } from './common/DatePeriodNavigation';
import { DataTable } from './common/DataTable';
import type { ColumnConfig } from './common/DataTable/types';
import { FilterBar } from './common/FilterControls';
import type { FilterDefinition } from './common/FilterControls';
import { ApiErrorBanner } from './common/ApiErrorBanner';
import {
  getBankTransactions,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type {
  BankTransaction,
  GetBankTransactionsParams,
  GetBankTransactions200,
  BankTransactionsSummary,
} from '../api/generated/model';
import { CopyableText } from './common/Copyable';
import MatchBlockersCell from './common/MatchBlockers';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import {
  methodLabel,
  paymentFormatLabel,
  methodOptions,
  paymentFormatOptions,
  formatDateTime,
  kindLabel,
  kindBadgeVariant,
} from '../utils/bankTransaction';

const FETCH_LIMIT = 50;

const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
};

// Filter keys understood by this component. The page persists these to URL;
// the modal use case persists nothing.
export const bankTransactionFilterKeys = [
  'id', 'direction', 'account_id', 'kind', 'method', 'payment_format',
  'trace_number', 'sender_trace', 'note', 'external_memo', 'search',
  'category_status', 'amount_min', 'amount_max',
] as const;

// Header-cell tri-state checkbox for the optional select-all column.
const TriStateCheckbox: React.FC<{ state: 'none' | 'some' | 'all'; ariaLabel?: string }> = ({ state, ariaLabel }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some';
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="form-check-input"
      checked={state === 'all'}
      readOnly
      tabIndex={-1}
      style={{ pointerEvents: 'none', margin: 0 }}
      aria-label={ariaLabel}
    />
  );
};

export interface BankTransactionsTableProps {
  // Initial state — only consulted on mount. Subsequent changes are local.
  initialDateRange?: { start_date: string; end_date: string };
  initialPeriod?: string;
  initialFilters?: Record<string, any>;
  initialSort?: { field: string; direction: 'asc' | 'desc' };

  // State change callbacks (e.g., for URL sync on the standalone page).
  onDateRangeChange?: (range: { start_date: string; end_date: string }, period: string) => void;
  onFiltersChange?: (filters: Record<string, any>) => void;
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void;

  // Candidate filter (modal use): when provided, an extra "Candidates only"
  // toggle is added to the filter bar, initially checked. When checked the
  // table renders the pre-fetched candidate BTs in-memory (no API hit) so
  // the user sees the suggested matches immediately. When unchecked, the
  // standard API-driven view kicks in and the user can search ALL BTs.
  candidateBankTransactions?: BankTransaction[];

  // Selection (modal use): when both selectedIds and onSelectionChange are
  // passed, a leading checkbox column is rendered and rows toggle selection
  // on click.
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;

  // Row click handler. When omitted, the row click does nothing (or toggles
  // selection if selection mode is active — selection wins over click).
  onRowClick?: (t: BankTransaction) => void;

  // Per-row "Revalidate" handler — when set, a small refresh button is
  // shown in the Match Issues column. The button calls this; the parent
  // is responsible for hitting the refetch endpoint and invalidating the
  // candidates query so blockers refresh in place.
  onRevalidate?: (t: BankTransaction) => Promise<void>;

  // Header customization.
  title?: string;
  titleIcon?: string;
  showDateNav?: boolean;
  countLabel?: (n?: number) => string;

  // DataTable preferences (always passed; required for column persist).
  preferencesKey: string;
  userId?: string;
  userPreferences?: any;

  // Bump to trigger a refetch from the parent (e.g., after a save).
  refreshKey?: number;
}

const BankTransactionsTable: React.FC<BankTransactionsTableProps> = ({
  initialDateRange,
  initialPeriod,
  initialFilters,
  initialSort,
  onDateRangeChange,
  onFiltersChange,
  onSortChange,
  candidateBankTransactions,
  selectedIds,
  onSelectionChange,
  onRowClick,
  onRevalidate,
  title = 'Transactions for',
  titleIcon = 'bi-bank2',
  showDateNav = true,
  countLabel,
  preferencesKey,
  userId,
  userPreferences,
  refreshKey,
}) => {
  const defaultDateRange = useMemo(() => {
    if (initialDateRange) return initialDateRange;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
    };
  }, [initialDateRange]);

  const candidatesAvailable = !!candidateBankTransactions && candidateBankTransactions.length > 0;

  const [period, setPeriod] = useState<string>(initialPeriod || 'month');
  const [dateRange, setDateRange] = useState<{ start_date: string; end_date: string }>(defaultDateRange);
  const [filters, setFilters] = useState<Record<string, any>>(() => ({ ...(initialFilters || {}) }));
  // Auto-select the "Show: Candidates" filter the first time candidates
  // become available. Done in an effect (not the useState initializer)
  // because candidates are typically fetched async — they're not in
  // hand at mount. Guarded by a ref so we don't re-set after the user
  // explicitly clears it.
  const autoSelectedCandidatesRef = useRef(false);
  useEffect(() => {
    if (candidatesAvailable && !autoSelectedCandidatesRef.current) {
      setFilters(prev => prev.candidates_only === undefined ? { ...prev, candidates_only: 'on' } : prev);
      autoSelectedCandidatesRef.current = true;
    }
  }, [candidatesAvailable]);
  const [showFilterBar, setShowFilterBar] = useState<boolean>(() => {
    return Object.entries(filters).some(([_, v]) => v);
  });
  const [sortField, setSortField] = useState(initialSort?.field || 'date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSort?.direction || 'desc');

  // Set of BT IDs currently being refetched. Read inside the renderCell
  // via ref so the column closures (captured once at table mount, per
  // feedback_datatable_renderCell_state) see the live state.
  const [revalidatingIds, setRevalidatingIds] = useState<Set<string>>(new Set());
  const revalidatingIdsRef = useRef(revalidatingIds);
  revalidatingIdsRef.current = revalidatingIds;
  const onRevalidateRef = useRef(onRevalidate);
  onRevalidateRef.current = onRevalidate;
  const handleRevalidateRow = useCallback(async (t: BankTransaction) => {
    const id = t.id || '';
    if (!id || !onRevalidateRef.current) return;
    setRevalidatingIds(prev => {
      const n = new Set(prev);
      n.add(id);
      return n;
    });
    try {
      await onRevalidateRef.current(t);
    } finally {
      setRevalidatingIds(prev => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  }, []);

  // Derived from the FilterBar value so toggling it via the standard
  // filter UX flips the table between candidate-only and full-fetch modes.
  const candidatesOnly = candidatesAvailable && filters.candidates_only === 'on';

  // Notify parents of state changes (URL sync etc.).
  useEffect(() => { onDateRangeChange?.(dateRange, period); }, [dateRange, period, onDateRangeChange]);
  useEffect(() => { onFiltersChange?.(filters); }, [filters, onFiltersChange]);
  useEffect(() => { onSortChange?.(sortField, sortDir); }, [sortField, sortDir, onSortChange]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(v => v).length,
    [filters]
  );

  const handleFilterChange = useCallback((field: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [field]: value || undefined }));
  }, []);
  const handleClearFilters = useCallback(() => setFilters({}), []);

  // Row identity. Shared with the hook's duplicate-detection and DataTable.
  const getRowKey = useCallback((t: BankTransaction) => t.id || '', []);

  const buildQueryParams = useCallback(
    (cursor: string | undefined) => {
      if (cursor) return { cursor, limit: FETCH_LIMIT };
      // 'all' mode is exclusive — never combine with start/end. Backend
      // returns 400 for the combination (defense in depth there too).
      const isAll = period === 'all';
      return {
        period,
        ...(!isAll && dateRange.start_date ? { start_date: dateRange.start_date } : {}),
        ...(!isAll && dateRange.end_date ? { end_date: dateRange.end_date } : {}),
        sort_field: sortField,
        sort_direction: sortDir,
        limit: FETCH_LIMIT,
        ...(filters.id ? { id: filters.id } : {}),
        ...(filters.direction ? { direction: filters.direction as any } : {}),
        ...(filters.account_id ? { account_id: filters.account_id } : {}),
        ...(filters.kind ? { kind: filters.kind } : {}),
        ...(filters.method ? { method: filters.method } : {}),
        ...(filters.payment_format ? { payment_format: filters.payment_format } : {}),
        ...(filters.trace_number ? { trace_number: filters.trace_number } : {}),
        ...(filters.sender_trace ? { sender_trace: filters.sender_trace } : {}),
        ...(filters.note ? { note: filters.note } : {}),
        ...(filters.external_memo ? { external_memo: filters.external_memo } : {}),
        ...(filters.counterparty ? { counterparty: filters.counterparty } : {}),
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.category_status ? { category_status: filters.category_status } : {}),
        ...(filters.amount_min ? { amount_min: filters.amount_min } : {}),
        ...(filters.amount_max ? { amount_max: filters.amount_max } : {}),
      };
    },
    [dateRange, filters, period, sortField, sortDir]
  );

  const {
    allItems: apiItems,
    totalCount: apiTotal,
    apiSummary: summary,
    isLoading,
    hasMore,
    handleLoadMore,
    reset,
    error: paginationError,
  } = usePaginatedQuery<BankTransaction, GetBankTransactions200, BankTransactionsSummary>({
    queryKey: `bank-transactions:${preferencesKey}`,
    buildQueryParams,
    queryFn: (params) => getBankTransactions(params as GetBankTransactionsParams),
    getRowKey,
    enabled: !candidatesOnly,
  });

  // Refresh when parent bumps refreshKey. Use reset() (which also wipes
  // the react-query cache for this hook's key) instead of setAllItems([]),
  // so a mutation that produces a byte-identical list response — e.g. a
  // per-row Resync that didn't change anything visible to the list query —
  // still triggers the accumulator effect to repopulate. See the reset()
  // doc comment in usePaginatedQuery.
  useEffect(() => {
    if (refreshKey === undefined) return;
    reset();
  }, [refreshKey, reset]);

  const accounts = summary?.accounts;

  const handlePeriodChange = (event: PeriodChangeEvent) => {
    setPeriod(event.navigationMode);
    setDateRange({
      start_date: event.dateRange.start_date.split('T')[0],
      end_date: event.dateRange.end_date.split('T')[0],
    });
  };

  // Selection handling (only enabled when both props provided).
  //
  // Why refs everywhere: DataTable memoises its `columnOrder` state from
  // the initial `columns` prop and never re-syncs on subsequent renders
  // (useColumnManagement.ts:138 — `useState(defaultColumns)`). That means
  // anything captured in our renderCell / onCellClick closures is frozen
  // at first render — selectedSet, selectedIds, displayedItems all go
  // stale the moment the user interacts. Refs are read live at render /
  // click time and side-step the trap.
  const selectionEnabled = !!selectedIds && !!onSelectionChange;
  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);
  const selectedSetRef = useRef(selectedSet);
  selectedSetRef.current = selectedSet;
  const displayedItemsRef = useRef<BankTransaction[]>([]);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  // The data the table renders. When "Candidates only" is on, render the
  // pre-fetched candidates directly (no API). Otherwise render the API page.
  const displayedItems: BankTransaction[] = candidatesOnly && candidateBankTransactions
    ? candidateBankTransactions
    : apiItems;
  displayedItemsRef.current = displayedItems;
  const displayedTotal = candidatesOnly && candidateBankTransactions
    ? candidateBankTransactions.length
    : apiTotal;
  const displayedHasMore = candidatesOnly ? false : hasMore;

  const toggleRow = useCallback((id: string) => {
    const cb = onSelectionChangeRef.current;
    if (!cb) return;
    const next = new Set(selectedSetRef.current);
    if (next.has(id)) next.delete(id); else next.add(id);
    cb(Array.from(next));
  }, []);

  const toggleAllOnPage = useCallback(() => {
    const cb = onSelectionChangeRef.current;
    if (!cb) return;
    const next = new Set(selectedSetRef.current);
    const ids = displayedItemsRef.current.map(t => t.id || '').filter(Boolean);
    const allSelected = ids.length > 0 && ids.every(id => next.has(id));
    if (allSelected) ids.forEach(id => next.delete(id));
    else ids.forEach(id => next.add(id));
    cb(Array.from(next));
  }, []);

  const filterDefs = useMemo((): FilterDefinition[] => {
    const defs: FilterDefinition[] = [];
    if (candidatesAvailable) {
      // Single-option select. "Candidates" narrows the table to the
      // pre-fetched candidate bank transactions; the dropdown's
      // auto-supplied "All" entry clears the filter. Reads as
      // "Show: Candidates" when active, just "Show" in the inactive chip.
      defs.push({
        type: 'select', field: 'candidates_only', label: 'Show', icon: 'bi-magic',
        options: [{ value: 'on', label: 'Candidates' }],
      });
    }
    defs.push(
      { type: 'text', field: 'id', label: 'ID', icon: 'bi-hash', placeholder: 'Substring match...' },
      {
        type: 'amount', field: 'amount', label: 'Amount', icon: 'bi-currency-dollar',
        directionField: 'direction',
        amountMinField: 'amount_min',
        amountMaxField: 'amount_max',
      },
      {
        type: 'multiselect', field: 'account_id', label: 'Account', icon: 'bi-bank2',
        options: (accounts || []).map(a => ({ value: a.id || '', label: a.name || a.id || '' })),
      },
      {
        type: 'multiselect', field: 'kind', label: 'Type', icon: 'bi-tag',
        options: (
          [
            'check_deposit',
            'incoming_domestic_wire',
            'incoming_international_wire',
            'external_transfer',
            'internal_transfer',
            'outgoing_payment',
            'expense_reimbursement',
            'credit_card_transaction',
            'credit_card_credit',
            'debit_card_transaction',
            'debit_card_credit',
            'treasury_transfer',
            'currency_cloud_return',
            'exogenous_wire_drawdown',
            'wire_fee',
            'card_international_transaction_fee',
            'card_international_transaction_fee_rebate',
            'card_international_transaction_fee_reversal',
            'card_international_transaction_fee_rebate_reversal',
            'personal_banking_subscription_fee',
            'billing_engine_subscription_fee',
            'other',
          ] as const
        ).map(v => ({ value: v, label: kindLabel(v) })),
      },
      { type: 'select', field: 'method', label: 'Method', icon: 'bi-credit-card', options: methodOptions },
      { type: 'select', field: 'payment_format', label: 'Format', icon: 'bi-diagram-3', options: paymentFormatOptions },
      { type: 'text', field: 'trace_number', label: 'Trace #', icon: 'bi-hash', placeholder: 'Exact trace number...' },
      { type: 'text', field: 'sender_trace', label: 'Sender Trace', icon: 'bi-hash', placeholder: 'Exact sender reference...' },
      { type: 'text', field: 'note', label: 'Note', icon: 'bi-chat-left-text', placeholder: 'Substring match on note...' },
      { type: 'text', field: 'external_memo', label: 'External Memo', icon: 'bi-file-text', placeholder: 'Substring match on external memo...' },
      { type: 'text', field: 'counterparty', label: 'Counterparty', icon: 'bi-person', placeholder: 'Substring match on counterparty...' },
      { type: 'text', field: 'search', label: 'Search', icon: 'bi-search', placeholder: 'Search notes, memo, traces...' },
      {
        type: 'multiselect', field: 'category_status', label: 'Category', icon: 'bi-tag',
        options: [
          { value: 'linked', label: 'Linked' },
          { value: 'pending', label: 'Pending' },
          { value: 'other', label: 'Not invoice payment' },
        ],
      },
    );
    return defs;
  }, [accounts, candidatesAvailable]);

  // Column set (selection column is conditionally prepended).
  const columns: ColumnConfig<BankTransaction>[] = useMemo(() => {
    const cols: ColumnConfig<BankTransaction>[] = [];
    if (selectionEnabled) {
      cols.push({
        key: '__select',
        label: '',
        width: 40,
        minWidth: 40,
        // renderHeader / renderCell read .current to dodge DataTable's
        // frozen-columns trap (see comment near selectedSetRef).
        renderHeader: () => {
          const set = selectedSetRef.current;
          const ids = displayedItemsRef.current.map(t => t.id || '').filter(Boolean);
          let state: 'none' | 'some' | 'all' = 'none';
          if (set.size > 0) {
            const allSelected = ids.length > 0 && ids.every(id => set.has(id));
            state = allSelected ? 'all' : 'some';
          }
          return <TriStateCheckbox state={state} ariaLabel="Select all on page" />;
        },
        headerOnClick: () => toggleAllOnPage(),
        // Cell click toggles. We deliberately don't also wire row-level
        // toggle here — the two would fire on the same click and cancel
        // each other out.
        onCellClick: (t) => t.id && toggleRow(t.id),
        renderCell: (t) => (
          <div className="d-flex justify-content-center" style={{ pointerEvents: 'none' }}>
            <Form.Check type="checkbox" checked={!!t.id && selectedSetRef.current.has(t.id)} readOnly tabIndex={-1} />
          </div>
        ),
      });
    }
    cols.push(
      {
        key: 'id',
        label: 'ID',
        width: 120,
        minWidth: 60,
        renderCell: (t) => t.id ? (
          <CopyableText value={t.id} as="code" className="d-block text-truncate" title={`${t.id} (click to copy)`} />
        ) : null,
      },
      {
        key: 'date',
        label: 'Date',
        width: 220,
        minWidth: 180,
        sortable: true,
        renderCell: (t) => <>{formatDateTime(t.posted_at || t.date)}</>,
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 120,
        sortable: true,
        sortValue: (t) => t.amount || 0,
        renderCell: (t) => (
          <span className="d-block text-end" style={{ color: (t.amount || 0) >= 0 ? '#198754' : '#dc3545' }}>
            {formatCurrency(t.amount)}
          </span>
        ),
      },
      { key: 'counterparty_name', label: 'Counterparty', width: 150, minWidth: 150, sortable: true },
      {
        key: 'kind', label: 'Type', width: 100, sortable: true,
        renderCell: (t) => <Badge bg={kindBadgeVariant(t.kind)}>{kindLabel(t.kind)}</Badge>,
      },
      { key: 'account_name', label: 'Account', width: 160, sortable: true },
      {
        key: 'note', label: 'Note', width: 150, minWidth: 100, sortable: true,
        renderCell: (t) => {
          const text = t.note || '';
          if (!text) return null;
          return <span className="text-muted" title={text}>{text.length > 60 ? text.substring(0, 60) + '...' : text}</span>;
        },
      },
      {
        key: 'external_memo', label: 'External Memo', width: 200, minWidth: 120, sortable: true,
        renderCell: (t) => {
          const text = t.external_memo || '';
          if (!text) return null;
          return <span className="text-muted" title={text}>{text.length > 60 ? text.substring(0, 60) + '...' : text}</span>;
        },
      },
      { key: 'method', label: 'Method', width: 90, sortable: true, renderCell: (t) => methodLabel(t.method) },
      {
        key: 'payment_format', label: 'Format', width: 180, minWidth: 120, sortable: true,
        renderCell: (t) => <span title={paymentFormatLabel(t.payment_format)}>{paymentFormatLabel(t.payment_format)}</span>,
      },
      {
        key: 'trace_number', label: 'Trace #', width: 160, minWidth: 120, sortable: true,
        renderCell: (t) => t.trace_number ? <code>{t.trace_number}</code> : null,
      },
      {
        key: 'sender_trace', label: 'Sender Trace', width: 160, minWidth: 120, sortable: true,
        renderCell: (t) => t.sender_trace ? <code>{t.sender_trace}</code> : null,
      },
      {
        key: 'linked_payments', label: 'Category', width: 240, minWidth: 160,
        renderCell: (t) => {
          const links = (t as any).linked_payments as Array<{
            customer_name?: string;
            applied_invoices?: Array<{ invoice_number?: string; invoice_id?: string }>;
          }> | undefined;
          if (links && links.length > 0) {
            return (
              <div className="d-flex flex-column gap-1" style={{ lineHeight: 1.2 }}>
                {links.map((lp, i) => {
                  const apps = lp.applied_invoices || [];
                  const invs = apps.map(a => a.invoice_number || a.invoice_id).filter(Boolean).join(', ');
                  return (
                    <div key={i} className="d-flex align-items-center gap-1 small">
                      <Badge bg="success">Linked</Badge>
                      {lp.customer_name && <span className="text-truncate" title={lp.customer_name}>{lp.customer_name}</span>}
                      {invs && <span className="text-muted">· #{invs}</span>}
                    </div>
                  );
                })}
              </div>
            );
          }
          if (t.category === 'other') {
            return <Badge bg="secondary">Not invoice payment</Badge>;
          }
          return null;
        },
      },
      {
        key: 'match_blockers', label: 'Match Issues', width: 280, minWidth: 200,
        renderCell: (t) => {
          const id = t.id || '';
          const isRevalidating = revalidatingIdsRef.current.has(id);
          const showButton = !!onRevalidateRef.current && !!id;
          return (
            <div className="d-flex align-items-center gap-1 flex-wrap">
              <MatchBlockersCell blockers={t.match_blockers} />
              {showButton && (
                <Button
                  size="sm"
                  variant={isRevalidating ? 'primary' : 'outline-secondary'}
                  className="d-inline-flex align-items-center gap-1 py-0 px-2"
                  disabled={isRevalidating}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleRevalidateRow(t);
                  }}
                  title="Refetch this bank transaction from its source and re-run the auto-linker"
                >
                  {isRevalidating
                    ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /><span>Revalidating…</span></>
                    : <><i className="bi bi-arrow-clockwise" /><span>Revalidate</span></>}
                </Button>
              )}
            </div>
          );
        },
      },
    );
    return cols;
  }, [selectionEnabled, selectedSet, displayedItems, toggleRow, toggleAllOnPage]);

  // Row click only fires in non-selection mode. When selection is on the
  // __select column's onCellClick is the sole toggle path — adding a
  // row-level handler too would double-fire and cancel out.
  const handleRowClick = useCallback((t: BankTransaction) => {
    if (onRowClick) onRowClick(t);
  }, [onRowClick]);
  // When selection is enabled, the __select column's onCellClick handles
  // toggle. Row-click is reserved for opening detail (when an onRowClick
  // is supplied) — that lets a candidate picker show checkboxes for
  // batch-link AND let the user open a row's detail by clicking elsewhere.
  // When no onRowClick is supplied in selection mode, row clicks do
  // nothing (only the checkbox toggles).
  const rowClickProp = selectionEnabled
    ? (onRowClick ? handleRowClick : undefined)
    : handleRowClick;

  return (
    <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'hidden' }}>
      <Card.Header className="bg-primary text-white flex-shrink-0">
        <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
              {title && (
                <>
                  <span className="text-primary fw-semibold">
                    <i className={`bi ${titleIcon} me-1`}></i>
                    {title}
                  </span>
                </>
              )}
              {showDateNav && (
                <DatePeriodNavigation
                  onPeriodChange={handlePeriodChange}
                  dateFormat="date-only"
                  initialNavigationMode={period as any}
                  initialDateRange={{ start_date: dateRange.start_date, end_date: dateRange.end_date }}
                />
              )}
              {showDateNav && <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>}
              <Button
                variant={activeFilterCount > 0 ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setShowFilterBar(!showFilterBar)}
              >
                <i className="bi bi-funnel me-1"></i>
                Filters
                {activeFilterCount > 0 && (
                  <span className="badge bg-header-control text-primary ms-1">{activeFilterCount}</span>
                )}
              </Button>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="bg-header-control rounded px-3 py-1">
              <span className="text-primary">
                {countLabel
                  ? countLabel(displayedTotal)
                  : (displayedTotal !== undefined ? `${displayedTotal} transactions` : 'No transactions found')}
              </span>
            </div>
          </div>
        </div>
        {showFilterBar && (
          <FilterBar
            filters={filters}
            filterDefs={filterDefs}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />
        )}
      </Card.Header>
      <ApiErrorBanner error={paginationError} />
      <Card.Body className="flex-grow-1 overflow-hidden p-0 d-flex flex-column" style={{ minHeight: 0 }}>
        <DataTable
          data={displayedItems}
          columns={columns}
          getRowKey={getRowKey}
          sortConfig={{ field: sortField, direction: sortDir }}
          onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
          onRowClick={rowClickProp}
          pagination={{ hasMore: displayedHasMore, limit: FETCH_LIMIT }}
          onLoadMore={handleLoadMore}
          isLoading={isLoading}
          preferencesKey={preferencesKey}
          userId={userId}
          userPreferences={userPreferences}
          maxHeight="100%"
          enableStickyHeader={true}
          enableColumnReorder={true}
          enableColumnResize={true}
          enableColumnVisibility={true}
          emptyState={<Alert variant="secondary" className="m-3">No transactions found.</Alert>}
        />
      </Card.Body>
    </Card>
  );
};

export default BankTransactionsTable;
